import { capitalize } from "@mui/material";
import { formatEventDatetime } from "@notemeal/utils-date-time";
import { Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import React from "react";
import GenericTable, { ICol } from "../../../components/universal/GenericTable";
import { StaffDashboardOrgMembershipFragment } from "../../../types";

export type AdminOrgMembershipTableRow = Omit<StaffDashboardOrgMembershipFragment, "user"> &
  StaffDashboardOrgMembershipFragment["user"] & {
    userTypeDisplay: string;
    teamDisplay: string;
  };

export const formatAxleNameList = (names: readonly string[]): string => {
  if (names.length === 0) {
    return "—";
  }
  return names.join(", ");
};

function formatTableCell(
  record: AdminOrgMembershipTableRow,
  key: keyof AdminOrgMembershipTableRow,
  locale: Locale,
  isTeamworksUserManagementEnabled: boolean,
): string | React.ReactNode {
  switch (key) {
    case "roles":
      return record.roles.map(r => capitalize(r)).join(", ");
    case "lastLogin":
      return record.lastLogin ? formatEventDatetime(new Date(record.lastLogin), locale) : "Never";
    case "lastRefresh":
      return record.lastRefresh ? formatEventDatetime(new Date(record.lastRefresh), locale) : "Never";
    case "isActive":
      return record.isActive ? "✓" : "";
    case "isAdmin":
      return record.isAdmin ? "✓" : "";
    case "teamworksId":
      return record.teamworksId !== null ? "✓" : "";
    case "athlete":
      return isTeamworksUserManagementEnabled ? record.primaryPhone ?? "" : record.athlete?.phoneNumber ?? "";
    default:
      return record[key];
  }
}

const cols: ICol<AdminOrgMembershipTableRow>[] = [
  {
    id: "firstName",
    isNumeric: false,
    disablePadding: false,
    label: "First Name",
  },
  {
    id: "lastName",
    isNumeric: false,
    disablePadding: false,
    label: "Last Name",
  },
  { id: "roles", isNumeric: false, disablePadding: false, label: "Roles" },
  {
    id: "username",
    isNumeric: false,
    disablePadding: false,
    label: "Username",
  },
  {
    id: "lastRefresh",
    isNumeric: false,
    disablePadding: false,
    label: "Last Refresh",
  },
  {
    id: "lastLogin",
    isNumeric: false,
    disablePadding: false,
    label: "Last Login",
  },
  { id: "isActive", isNumeric: false, disablePadding: false, label: "Active?" },
  { id: "isAdmin", isNumeric: false, disablePadding: false, label: "Admin?" },
  { id: "teamworksId", isNumeric: false, disablePadding: false, label: "Teamworks?" },
  { id: "axlePersonDocumentId", isNumeric: false, disablePadding: false, label: "Axle Person ID" },
];

// User Type and Team are resolved from Axle, so they're only meaningful for Axle-synced orgs.
// For non-synced orgs we omit them entirely rather than render a column of dashes.
const axleCols: ICol<AdminOrgMembershipTableRow>[] = [
  { id: "userTypeDisplay", isNumeric: false, disablePadding: false, label: "User Type" },
  { id: "teamDisplay", isNumeric: false, disablePadding: false, label: "Team" },
];

const buildCols = (isAxleSynced: boolean): ICol<AdminOrgMembershipTableRow>[] => {
  if (!isAxleSynced) {
    return cols;
  }
  const afterRoles = cols.findIndex(c => c.id === "roles") + 1;
  return [...cols.slice(0, afterRoles), ...axleCols, ...cols.slice(afterRoles)];
};

export interface OrgMembershipTableProps {
  tableRows: AdminOrgMembershipTableRow[];
  onClickAddEntry: () => void;
  onClickRow: (row: AdminOrgMembershipTableRow) => void;
  isTeamworksUserManagementEnabled?: boolean;
}

export const OrgMembershipTable = ({ tableRows, onClickRow, onClickAddEntry, isTeamworksUserManagementEnabled = false }: OrgMembershipTableProps) => {
  return (
    <GenericTable
      initRowsPerPage={25}
      size="small"
      tableRows={tableRows}
      tableCols={buildCols(isTeamworksUserManagementEnabled)}
      addButtonText={"Create Org Membership"}
      onClickAddEntry={onClickAddEntry}
      onClickRow={onClickRow}
      formatTableCell={(record, key) => formatTableCell(record, key, enUS, isTeamworksUserManagementEnabled)} // TODO: Locale Staff view fixes
      defaultOrderBy={"lastLogin"}
      hideAddButton={isTeamworksUserManagementEnabled}
    />
  );
};
